import React, { useEffect } from "react";
import Link from "next/link";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useMediaQuery, useTheme } from "@mui/material";

import useAsync, {
  IUseAsyncReturnProps,
} from "@packages/common/src/custom-hooks/useAsync";
import GenericResponseHandler from "@packages/common/src/components/others/GenericResponseHandler";

import AppsService from "../../services/appsService";
import AppsList from "./AppsList";
import AppsListSkeleton from "./AppsListSkeleton";
import UseCasesTitle from "./UseCasesTitle";
import { NSApps } from "../types";
import { neutral } from "../../../common/config/colors";

const appsService = new AppsService();
const UseCasesListItem: React.FC<NSApps.ICategoryItem> = ({
  id,
  name,
  slug,
  image,
}) => {
  const theme = useTheme();
  const isDownMd = useMediaQuery(theme.breakpoints.down("md"));
  const {
    run,
    data: apps,
    status,
    error,
  }: IUseAsyncReturnProps<NSApps.IAppsResponse> = useAsync();

  useEffect(() => {
    run(appsService.apps({ categories: [slug], page_size: isDownMd ? 4 : 8 }));
  }, [isDownMd, run, slug]);

  return (
    <Box
      key={id}
      sx={{
        bgcolor: "#F5F5F7",
        borderRadius: 5,
        py: 9,
        px: { xs: 3, sm: 7.1 },
        mb: { xs: 6, md: 12 },
        "& a": {
          textDecoration: "none",
        },
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr auto 1fr",
          gridTemplateAreas: `
          "title . . button"
          "apps apps apps apps"
        `,
          [theme.breakpoints.down("md")]: {
            gridTemplateColumns: "1fr",
            gridTemplateRows: "1fr auto 1fr",
            gridTemplateAreas: `
            "title"
            "apps"
            "button"
        `,
          },
        }}
      >
        <UseCasesTitle name={name} image={image} />
        <Box sx={{ gridArea: "apps", pt: 6, pb: { xs: 6, md: 0 } }}>
          <GenericResponseHandler
            status={status}
            error={error}
            skeleton={<AppsListSkeleton />}
          >
            <>{!!apps && <AppsList apps={apps} />}</>
          </GenericResponseHandler>
        </Box>
        <Box
          sx={{
            gridArea: "button",
            color: neutral[900],
            [theme.breakpoints.down("md")]: {
              display: "flex",
              justifyContent: "center",
            },
          }}
        >
          <Link href={`/apps/categories/${slug}`}>
            <a>
              <Button
                endIcon={<ArrowForwardIcon />}
                sx={{ color: neutral[900] }}
              >
                View All
              </Button>
            </a>
          </Link>
        </Box>
      </Box>
    </Box>
  );
};

export default UseCasesListItem;
